import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { EmptyArray } from 'lib/array/arrayUtils'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import isLoyaltyProductTypeBonusApplicable from 'loyalty/lib/pointsCalculation/isLoyaltyProductTypeBonusApplicable'
import getLoyaltyEarnKey, { getLoyaltyBedbankPackageId } from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import { createSelector } from 'reselect'
import { getPromoKeyForLoyaltyItem } from 'lib/promo/getPromoKeyForLoyaltyItem'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'

/**
 * Earn for an applied property-rate package upgrade: the customer earns on the BASE rate, so return the
 * source rate's product type / earn key / margin (the earn price is already source via `itemView.totals`).
 * Matters for cross-service (LPC↔bedbank) upgrades; a no-op for same-service.
 */
function getPropertyRateUpgradeSourceEarn(
  item: App.Checkout.BaseAccommodationItem<string>,
  earnKeyOptions: { propertyCountryCode: string | undefined, propertyStateProvince: string | undefined, currentRegionCode: string },
): { loyaltyProductType: 'hotel_lpc' | 'hotel_lpp_eps', earnKey: string | undefined, mx: string | undefined } | undefined {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade) return

  if (upgrade.source.serviceType === 'lpc') {
    return {
      loyaltyProductType: 'hotel_lpc',
      // Source ultra-lux is captured at apply time (the target offer may not report it cross-service).
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpc', options: { ...earnKeyOptions, isUltraLux: upgrade.sourceIsUltraLux ?? false } }),
      mx: upgrade.sourcePricing.totals.mx,
    }
  }

  return {
    loyaltyProductType: 'hotel_lpp_eps',
    earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { ...earnKeyOptions, propertyId: upgrade.source.propertyId, supplierCode: upgrade.sourceSupplierCode } }),
    mx: upgrade.sourcePricing.totals.mx,
  }
}

const getCheckoutAccommodationPointsCalculationRequests = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.checkout.promo?.loyaltyPointsBonusResultV1,
  (accommodationOfferView, currentRegionCode, promoLoyaltyBonusResultV1): Array<App.LoyaltyPointsEarnCalculationRequest> => {
    if (!accommodationOfferView.hasRequiredData) return EmptyArray

    const pointsCalculations: Array<App.LoyaltyPointsEarnCalculationRequest> = []

    accommodationOfferView.data.forEach((data) => {
      if (!data.offer) return
      const { offer } = data
      const hotelLocation = data.locationCountryCode
      data.itemViews.forEach((itemView) => {
        if (!itemView.loyalty.productType) return
        const memberPrice = itemView.totals.memberPrice
        const upgradeSourceEarn = (itemView.kind === 'le' || itemView.kind === 'bedbank')
          ? getPropertyRateUpgradeSourceEarn(itemView.item, {
              propertyCountryCode: hotelLocation,
              propertyStateProvince: data.locationStateProvince,
              currentRegionCode,
            })
          : undefined

        if (itemView.kind === 'le') {
          if (isLoyaltyProductTypeBonusApplicable(itemView.loyalty.productType)) {
            pointsCalculations.push({
              price: itemView.totals.price + itemView.totals.surcharge,
              memberPrice: memberPrice > 0 ? memberPrice + itemView.totals.surcharge : 0,
              loyaltyProductType: upgradeSourceEarn?.loyaltyProductType ?? itemView.loyalty.productType,
              mx: upgradeSourceEarn?.mx ?? getHotelEncryptedMargin(offer.type, itemView.loyaltyUpgradeAvailability?.originPackage ?? itemView.pkg),
              offerId: data.offerId,
              packageId: itemView.packageId,
              ratePlanId: itemView.pkg?.roomRate?.ratePlanId,
              itemId: itemView.item.itemId,
              earnKey: upgradeSourceEarn?.earnKey ?? (itemView.loyalty.productType === 'hotel_lpc'
                ? getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpc', options: { propertyCountryCode: hotelLocation, propertyStateProvince: data.locationStateProvince, currentRegionCode, isUltraLux: isLpcHotelOffer(offer) && !!offer.property.isUltraLux } })
                : itemView.loyalty.productType === 'hotel_flash'
                  ? getLoyaltyEarnKey({ loyaltyProductType: 'hotel_flash', options: { propertyCountryCode: hotelLocation, propertyStateProvince: data.locationStateProvince, currentRegionCode } })
                  : undefined),
              promoKey: getPromoKeyForLoyaltyItem(promoLoyaltyBonusResultV1, { filter: { itemId: itemView.item.itemId } }),
              nights: itemView.item.duration,
              orderId: 'orderId' in itemView.item ? itemView.item.orderId : undefined,
              orderItemId: 'orderItemId' in itemView.item ? itemView.item.orderItemId : undefined,
            })
          } else {
            pointsCalculations.push({
              price: itemView.totals.price + itemView.totals.surcharge,
              memberPrice: memberPrice > 0 ? memberPrice + itemView.totals.surcharge : 0,
              loyaltyProductType: itemView.loyalty.productType,
              mx: getHotelEncryptedMargin(offer.type, itemView.loyaltyUpgradeAvailability?.originPackage ?? itemView.pkg),
              itemId: itemView.item.itemId,
              promoKey: getPromoKeyForLoyaltyItem(promoLoyaltyBonusResultV1, { filter: { itemId: itemView.item.itemId } }),
              nights: itemView.item.duration,
              orderId: 'orderId' in itemView.item ? itemView.item.orderId : undefined,
              orderItemId: 'orderItemId' in itemView.item ? itemView.item.orderItemId : undefined,
            })
          }
        }

        if (itemView.kind === 'bedbank') {
          pointsCalculations.push({
            price: itemView.totals.price + itemView.totals.surcharge,
            memberPrice: memberPrice > 0 ? memberPrice + itemView.totals.surcharge : 0,
            loyaltyProductType: upgradeSourceEarn?.loyaltyProductType ?? itemView.loyalty.productType,
            mx: upgradeSourceEarn?.mx ?? itemView.rate?.mx,
            itemId: itemView.item.itemId,
            packageId: getLoyaltyBedbankPackageId(itemView.propertyId, itemView.rate?.supplierCode),
            offerId: data.offerId,
            earnKey: upgradeSourceEarn?.earnKey ?? getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode: hotelLocation, propertyStateProvince: data.locationStateProvince, currentRegionCode, propertyId: itemView.propertyId, supplierCode: itemView.rate?.supplierCode } }),
            promoKey: getPromoKeyForLoyaltyItem(promoLoyaltyBonusResultV1, { filter: { itemId: itemView.item.itemId } }),
            nights: itemView.item.duration,
            orderId: 'orderId' in itemView.item ? itemView.item.orderId : undefined,
            orderItemId: 'orderItemId' in itemView.item ? itemView.item.orderItemId : undefined,
          })
        }

        if (itemView.kind === 'cruise') {
          pointsCalculations.push({
            itemId: itemView.item.itemId,
            loyaltyProductType: itemView.loyalty.productType,
            memberPrice,
            price: itemView.totals.price || itemView.currentBookingStepLowestPrice || 0,
            offerId: data.offerId,
            packageId: itemView.packageId,
            promoKey: getPromoKeyForLoyaltyItem(promoLoyaltyBonusResultV1, { filter: { itemId: itemView.item.itemId } }),
            nights: offer.type === 'cruise' ? offer.duration : undefined,
            earnKey: offer.type === 'cruise' ? offer.earnKey : undefined,
          })
        }

        if (itemView.kind === 'tourV2') {
          pointsCalculations.push({
            price: itemView.totals.price,
            memberPrice,
            loyaltyProductType: itemView.loyalty.productType,
            itemId: itemView.item.itemId,
            packageId: itemView.packageId,
            offerId: itemView.offerId,
            promoKey: getPromoKeyForLoyaltyItem(promoLoyaltyBonusResultV1, { filter: { itemId: itemView.item.itemId } }),
            nights: itemView.item.duration,
          })
        }
      })
    })

    return pointsCalculations
  },
)

export default getCheckoutAccommodationPointsCalculationRequests
